import {useEffect} from "react";

const BOOLEAN_ARIA_ATTRIBUTES = [
  "aria-checked",
  "aria-disabled",
  "aria-expanded",
  "aria-hidden",
  "aria-pressed",
  "aria-required",
  "aria-selected",
];

const hasAccessibleName = (element) => {
  if (element.getAttribute("aria-label")?.trim()) return true;
  if (element.getAttribute("aria-labelledby")?.trim()) return true;
  if (element.getAttribute("title")?.trim()) return true;
  if (element.textContent?.trim()) return true;
  return Boolean(element.querySelector("img[alt]:not([alt=''])"));
};

const ensureControlName = (element) => {
  const tagName = element.tagName.toLowerCase();
  const type = (element.getAttribute("type") || "").toLowerCase();
  if (tagName === "input" && type === "hidden") return;

  const id = element.getAttribute("id");
  const hasLabel =
    (id && document.querySelector(`label[for="${CSS.escape(id)}"]`)) ||
    element.closest("label");
  if (hasLabel || element.getAttribute("aria-label")?.trim()) return;
  if (element.getAttribute("aria-labelledby")?.trim()) return;

  const fallback =
    element.getAttribute("placeholder") ||
    element.getAttribute("name") ||
    (tagName === "select" ? "Sélection" : "Champ de formulaire");
  element.setAttribute("aria-label", fallback);
};

const repairAccessibility = (root = document) => {
  const descendants = (selector) => {
    const elements = Array.from(root.querySelectorAll?.(selector) || []);
    if (root.matches?.(selector)) elements.unshift(root);
    return elements;
  };

  descendants("[aria-label]").forEach((element) => {
    const value = element.getAttribute("aria-label");
    if (!value || ["undefined", "null"].includes(value.trim().toLowerCase())) {
      element.removeAttribute("aria-label");
    }
  });

  descendants("button, a[href], [role='button']").forEach((element) => {
    if (!hasAccessibleName(element)) element.setAttribute("aria-label", "Action");
  });

  descendants("input, select, textarea").forEach(ensureControlName);

  BOOLEAN_ARIA_ATTRIBUTES.forEach((attribute) => {
    descendants(`[${attribute}]`).forEach((element) => {
      const value = element.getAttribute(attribute);
      if (value !== "true" && value !== "false" && value !== "mixed") {
        element.removeAttribute(attribute);
      }
    });
  });
};

const AccessibilityGuard = () => {
  useEffect(() => {
    repairAccessibility();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(({addedNodes}) => {
        addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) repairAccessibility(node);
        });
      });
    });
    observer.observe(document.body, {childList: true, subtree: true});
    return () => observer.disconnect();
  }, []);

  return null;
};

export default AccessibilityGuard;
